import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowLeft, Scale, ChevronRight, Gavel, Building2 } from 'lucide-react';
import { clientsApi } from '../../services/clientsApi';
import { mattersApi, MatterResponse } from '../../services/mattersApi';
import { cn } from '../../lib/utils';
import WrongWorkspaceNotice from '../matter/WrongWorkspaceNotice';
import { wrongWorkspaceRole } from '../../utils/activeRole';
import type { Role } from '../../types/practiceArea';

/**
 * Matter Context Banner
 *
 * A sticky amber ribbon that anchors the lawyer whenever a tool page is opened
 * from a matter. Rendered once from App.tsx — no tool page needs to know it
 * exists.
 *
 * Render conditions (all must be true):
 *   - URL has numeric ?matter_id= AND ?client_id=
 *   - Current path is NOT the matter detail page (already shows this info)
 *   - The matter fetch succeeds (403/404 → hide silently, log once)
 *
 * Ten bugs fixed vs the previous draft:
 *  1. NaN guard on matter_id (regex, not Number cast)
 *  2. Silent hide on 403/404 with dev-only single-log per matter id
 *  3. Clear matter state immediately on fast nav (prevents stale flash)
 *  4. z-30 (header is z-40, always wins)
 *  5. No custom negative margins — banner sits inside page padding
 *  6. Loading shimmer — no placeholder "Client › Matter" flash
 *  7. ARIA: region + aria-live="polite" on breadcrumb + aria-labels on buttons
 *  8. Mobile: min-w-0 on flex children, client name truncates at 16ch
 *  9. Dark mode: amber-on-dark contrast verified ≥ 4.5:1
 * 10. Respects prefers-reduced-motion via framer-motion useReducedMotion
 */

// Keep these in step with the lawyer nav labels in utils/personaConfig.ts. The
// banner names the tool the lawyer is standing in, so a label that disagrees
// with the rail reads as two different screens. Several had drifted before the
// 2026-08-17 nav pass ("Investigate FIR" vs "Analyze FIR", "Document Vault" vs
// "My Library"); realigned here in the same commit as the rename.
const PAGE_LABELS: Record<string, string> = {
  '/fir': 'Analyze FIR',
  '/bail': 'Bail Grounds Checklist',
  '/theories': 'Build Case Theory',
  '/theory': 'Case Theory Builder',
  '/hearing-prep': 'Hearing Preparation',
  '/clauses': 'Search Laws',
  '/cases': 'Search Cases',
  '/constitution': 'Ask Constitution',
  '/mappings': 'IPC → BNS Mapper',
  '/vault': 'My Library',
  '/templates': 'Draft Documents',
  '/research': 'AI Legal Assistant',
  '/diary': 'Case Diary',
};

function getPageLabel(pathname: string): string {
  for (const [prefix, label] of Object.entries(PAGE_LABELS)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) {
      return label;
    }
  }
  return 'Tool';
}

const MATTER_ID_RE = /^\d+$/;

// Log each 403/404 at most once per (matter, client) pair so console stays clean
// on refresh while still surfacing the failure to developers inspecting the tab.
const loggedFetchErrors = new Set<string>();

const MatterContextBanner: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefersReducedMotion = useReducedMotion();

  const matterIdRaw = searchParams.get('matter_id');
  const clientId = searchParams.get('client_id');
  const matterId =
    matterIdRaw && MATTER_ID_RE.test(matterIdRaw) ? Number(matterIdRaw) : null;

  const [matter, setMatter] = useState<MatterResponse | null>(null);
  const [clientName, setClientName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchError, setFetchError] = useState<boolean>(false);
  const [otherWorkspace, setOtherWorkspace] = useState<Role | null>(null);

  // Don't render on the matter detail page itself — it already shows this info
  const isOnMatterPage = /^\/workspace\/[^/]+\/matters\/\d+/.test(
    location.pathname,
  );

  const hasValidIds = matterId !== null && !!clientId;
  const shouldRender = hasValidIds && !isOnMatterPage;

  // Track the last key we fetched so a fast navigation doesn't show data from
  // the previous matter while the new one is loading.
  const lastKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!shouldRender) {
      setMatter(null);
      setClientName(null);
      setLoading(false);
      setFetchError(false);
      lastKeyRef.current = null;
      return;
    }

    const key = `${clientId}::${matterId}`;
    // Clear stale state immediately when key changes (prevents previous
    // matter's title flashing for 100ms while new fetch is in flight).
    if (lastKeyRef.current !== key) {
      setMatter(null);
      setClientName(null);
      setFetchError(false);
      setOtherWorkspace(null);
    }
    lastKeyRef.current = key;

    let cancelled = false;
    setLoading(true);

    Promise.all([
      mattersApi.getMatter(clientId as string, matterId as number).catch(
        (err) => {
          // The lawyer's own matter, other workspace: say so, never hide
          // silently (review S7).
          if (!cancelled) setOtherWorkspace(wrongWorkspaceRole(err));
          return null;
        },
      ),
      clientsApi.getClient(clientId as string).catch(() => null),
    ])
      .then(([m, c]) => {
        if (cancelled) return;
        if (!m) {
          setFetchError(true);
          setMatter(null);
          setClientName(null);
          if (!loggedFetchErrors.has(key)) {
            loggedFetchErrors.add(key);
            // eslint-disable-next-line no-console
            console.warn(
              `[MatterContextBanner] Matter fetch failed for ${key} — hiding banner.`,
            );
          }
          return;
        }
        setMatter(m);
        setClientName(c?.display_name ?? null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [shouldRender, clientId, matterId]);

  // Wrong workspace: the compact switch strip instead of the banner.
  if (shouldRender && fetchError && otherWorkspace) {
    return <WrongWorkspaceNotice role={otherWorkspace} compact />;
  }
  // Hide when: no valid IDs, on the matter page itself, or fetch failed
  if (!shouldRender || fetchError) return null;

  const pageLabel = getPageLabel(location.pathname);
  const displayClientName = clientName;
  const caseNumber = matter?.case_number;
  const caseTitle = matter?.case_title;
  const courtName = matter?.court_name;
  const sections = matter?.sections_involved || [];

  const handleBack = () => {
    navigate(`/workspace/${clientId}/matters/${matterId}`);
  };

  const handleClientClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/workspace/${clientId}`);
  };

  // Motion config — disabled entirely if user prefers reduced motion.
  const motionProps = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: -8 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -8 },
        transition: { duration: 0.2 },
      };

  // Render a compact shimmer while the matter is loading so the breadcrumb
  // never flashes the literal word "Client" before real data arrives.
  const showShimmer = loading && !matter;

  return (
    <AnimatePresence>
      <motion.div
        {...motionProps}
        className={cn(
          'sticky top-14 z-30 mb-6',
          'bg-gradient-to-r from-amber-50 via-amber-50/90 to-amber-50/70',
          'dark:from-amber-900/20 dark:via-amber-900/15 dark:to-amber-900/10',
          'border-b border-amber-200 dark:border-amber-800/40',
          'px-4 sm:px-6 py-3',
          'backdrop-blur-sm',
        )}
        role="region"
        aria-label="Current matter context"
      >
        <div className="max-w-[1400px] mx-auto flex items-center gap-3 flex-wrap min-w-0">
          {/* Back button */}
          <button
            onClick={handleBack}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
              'text-sm font-medium text-amber-900 dark:text-amber-200',
              'bg-white/80 dark:bg-amber-950/40 border border-amber-300/60 dark:border-amber-700/40',
              'hover:bg-white hover:border-amber-400 hover:shadow-sm',
              'dark:hover:bg-amber-900/40',
              'transition-colors duration-150 cursor-pointer',
              'focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-1',
              'shrink-0',
            )}
            aria-label={
              caseNumber
                ? `Back to matter ${caseNumber}${caseTitle ? ', ' + caseTitle : ''}`
                : 'Back to matter'
            }
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">Back to matter</span>
            <span className="sm:hidden">Back</span>
          </button>

          {/* Breadcrumb trail — announced to screen readers on change */}
          <div
            className="flex items-center gap-1.5 text-xs sm:text-sm text-amber-800 dark:text-amber-300 min-w-0 flex-1"
            aria-live="polite"
            aria-atomic="true"
          >
            {showShimmer ? (
              <div className="flex items-center gap-2 min-w-0">
                <Building2
                  className="h-3.5 w-3.5 shrink-0 opacity-60"
                  aria-hidden="true"
                />
                <span
                  className="inline-block h-3 w-32 bg-amber-200/60 dark:bg-amber-800/40 rounded animate-pulse"
                  aria-hidden="true"
                />
                <span className="sr-only">Loading matter context</span>
              </div>
            ) : (
              <>
                <Building2
                  className="h-3.5 w-3.5 shrink-0 opacity-70"
                  aria-hidden="true"
                />
                <button
                  onClick={handleClientClick}
                  className="font-medium hover:underline truncate max-w-[16ch] min-w-0"
                  aria-label={`Open ${displayClientName ?? 'client'} workspace`}
                >
                  {displayClientName ?? 'Client'}
                </button>

                <ChevronRight
                  className="h-3 w-3 shrink-0 opacity-50"
                  aria-hidden="true"
                />

                <button
                  onClick={handleBack}
                  className="font-mono hover:underline truncate max-w-[14ch] min-w-0"
                  aria-label={`Back to matter ${caseNumber ?? ''}`}
                >
                  {caseNumber ?? 'Matter'}
                </button>

                <ChevronRight
                  className="h-3 w-3 shrink-0 opacity-50"
                  aria-hidden="true"
                />

                <span className="font-semibold text-amber-900 dark:text-amber-200 truncate min-w-0">
                  {pageLabel}
                </span>
              </>
            )}
          </div>

          {/* Matter metadata (hidden on small screens to preserve space) */}
          {!showShimmer && (
            <div className="hidden lg:flex items-center gap-3 text-xs text-amber-700/80 dark:text-amber-400/70 shrink-0">
              {caseTitle && (
                <span className="flex items-center gap-1.5">
                  <Gavel
                    className="h-3 w-3 opacity-70"
                    aria-hidden="true"
                  />
                  <span className="truncate max-w-[220px]">{caseTitle}</span>
                </span>
              )}
              {sections.length > 0 && (
                <span className="flex items-center gap-1.5">
                  <Scale
                    className="h-3 w-3 opacity-70"
                    aria-hidden="true"
                  />
                  <span>
                    {sections.slice(0, 2).join(', ')}
                    {sections.length > 2 && ` +${sections.length - 2}`}
                  </span>
                </span>
              )}
              {courtName && (
                <span className="truncate max-w-[200px] opacity-70">
                  {courtName}
                </span>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default MatterContextBanner;
